// OAuth2 标准授权码流程的「回跳落地页」。
//
// IdP 登录成功后,gitman 后端的 callback 端点会 302 回到本页:
//   /oauth/callback?code=...    (一次性兑换码,POST 换真实 token)
//   /oauth/callback?error=...   (授权失败,展示错误并允许重试)
//
// 本页逻辑:
//   1. 读 query 的 code / error。
//   2. 有 code → 调 oauthExchangeLogin(code) 换 token → 跳转首页。
//   3. 有 error → 展示错误 + 「返回登录页」按钮。
import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Title3,
} from '@fluentui/react-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { ROUTES, defaultPostLoginPath } from '@/routes';
import { explainError } from '@/api/client';

const useStyles = makeStyles({
  fullPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: '420px',
    maxWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
  },
  cardBody: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    width: '56px',
    height: '56px',
  },
  message: {
    width: '100%',
  },
});

export function OAuthCallbackPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const oauthExchangeLogin = useAuthStore((s) => s.oauthExchangeLogin);

  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    const code = params.get('code');
    const err = params.get('error');

    // 授权失败:后端把错误塞进 ?error=(url-encoded)
    if (err) {
      setStatus('error');
      try {
        setErrorMsg(decodeURIComponent(err));
      } catch {
        setErrorMsg(err);
      }
      return;
    }

    // 没有 code 也没有 error:非法回跳
    if (!code) {
      setStatus('error');
      setErrorMsg('缺少授权码,无法完成登录。');
      return;
    }

    // 兑换一次性 code → 真实 token → 跳转首页
    (async () => {
      try {
        await oauthExchangeLogin(code);
        navigate(
          defaultPostLoginPath(!!useAuthStore.getState().user?.is_admin),
          { replace: true },
        );
      } catch (e) {
        setStatus('error');
        setErrorMsg(explainError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.fullPage}>
      <Card className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.brand}>
            <img src="/icons/logo-64.png" alt="Ming-GIT" className={styles.logo} />
            <Title3 as="h1">Ming-GIT</Title3>
          </div>

          {status === 'processing' ? (
            <>
              <Spinner label="正在完成 SSO 登录…" />
            </>
          ) : (
            <MessageBar intent="error" className={styles.message}>
              <MessageBarBody>
                <MessageBarTitle>登录失败</MessageBarTitle>
                {errorMsg}
              </MessageBarBody>
            </MessageBar>
          )}

          {status === 'error' && (
            <Button
              appearance="primary"
              onClick={() => navigate(ROUTES.login, { replace: true })}
            >
              返回登录页
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
